/**
 * SIL-472 — Environmental Overview: THE ONE MODULE every sample-tagged
 * widget's fixture lives in. Per
 * resources/plans/sil-461-dashboard-data-readiness.md's demo-verdict table,
 * prod had zero rows backing these widgets at the time (marketplace_transaction
 * was empty; use-phase history is still 2 of the >=13 months a YoY
 * comparison needs) — the honest render is sample data, clearly tagged.
 *
 * SIL-471 wired the AVOIDED KPI tile + avoided-events tape to real per-org
 * data once `marketplace_transaction` gained COMPLETED rows — `SAMPLE_
 * AVOIDED_KGCO2E`/`SAMPLE_AVOIDED_TRANSFER_COUNT`/`SAMPLE_AVOIDED_EVENTS`
 * below are now the FALLBACK those two widgets render for an org with zero
 * completed sales, not the only render path — see
 * `hooks/useEnvironmentalOverview.ts`'s `useKpiAvoided`/`useAvoidedEventsTape`
 * for the real-vs-sample selection logic. SIL-487 deleted the YoY table's
 * sample fixture entirely (no fallback) now that real
 * `asset_use_phase_snapshot` history exists for every org with at least one
 * computed month — see `useYoyTable` in `useEnvironmentalOverview.ts`. (The
 * INTENSITY KPI tile that used to read from this module was dropped in
 * SIL-488.)
 *
 * Swapping a still-sample-only widget to real data later is a one-line
 * change: point the corresponding hook in `hooks/useEnvironmentalOverview.ts`
 * at a real query instead of this module, nothing else on the page changes
 * (every widget already renders its own basis chip and does not special-case
 * 'sample' vs 'derived' beyond that chip).
 *
 * Never import this module for anything the API can serve — see the
 * REAL-widget hooks in `useEnvironmentalOverview.ts` for those.
 */
import type { EnvironmentalBasis } from '@siliconx/shared';

export const SAMPLE_BASIS: EnvironmentalBasis = 'sample';

/** AVOIDED KPI tile — SIL-459 hard-quarantined. */
export const SAMPLE_AVOIDED_KGCO2E = 2_410_000;
export const SAMPLE_AVOIDED_TRANSFER_COUNT = 63;

export type SampleAvoidedEvent = {
  id: string;
  timestamp: string;
  assetClass: string;
  model: string;
  event: string;
  avoidedKgco2e: number;
};

export const SAMPLE_AVOIDED_EVENTS: SampleAvoidedEvent[] = [
  {
    id: 'sample-1',
    timestamp: '2026-07-29T14:02:00Z',
    assetClass: 'GPU server',
    model: 'H100 SXM 80GB node',
    event: 'transfer settled — EU buyer',
    avoidedKgco2e: 3520,
  },
  {
    id: 'sample-2',
    timestamp: '2026-07-28T09:47:00Z',
    assetClass: 'GPU server',
    model: 'A100 SXM 80GB node',
    event: 'transfer settled — US buyer',
    avoidedKgco2e: 3410,
  },
  {
    id: 'sample-3',
    timestamp: '2026-07-24T16:31:00Z',
    assetClass: 'GPU server',
    model: 'A100 PCIe 40GB node',
    event: 'transfer settled — US buyer',
    avoidedKgco2e: 2870,
  },
  {
    id: 'sample-4',
    timestamp: '2026-07-21T11:15:00Z',
    assetClass: 'GPU server',
    model: 'L40S PCIe node',
    event: 'transfer settled — APAC buyer',
    avoidedKgco2e: 2120,
  },
];

/**
 * SIL-475 — v0 Scope 3 upstream/downstream section, downstream rows (cat 11
 * use-of-sold-products, cat 12 end-of-life-of-sold-products). Per
 * resources/plans/sil-461-dashboard-data-readiness.md, prod has zero
 * `marketplace_transaction` rows for ANY org — a seller with no closed sales
 * has genuinely 0 cat 11/12 this period. This ticket's dispatch chose the
 * SAMPLE-tagged illustrative treatment (matching every other SAMPLE widget
 * already on this page) over the readiness doc's honest-empty-state
 * alternative, so Meta's downstream half of the upstream/downstream picture
 * it asked for isn't a hole — always rendered with the 'sample' basis chip,
 * never mistaken for a real figure.
 */
export const SAMPLE_SCOPE3_CAT11_KGCO2E = 184_000;
/** Cat 11's Box 11.2 intensity companion metric (annual kg CO2e per unit in
 * use) — shown next to the lifetime total per the matrix's mitigation for
 * the "longer lifetimes raise reported Cat 11" hazard (S3 p115). */
export const SAMPLE_SCOPE3_CAT11_INTENSITY_KGCO2E_PER_UNIT_YR = 410;
export const SAMPLE_SCOPE3_CAT12_KGCO2E = 620;

/**
 * Monthly Scope 3 flow-by-category series for the small-multiples monthly
 * chart (demo-feedback decision, prototyped on jarriett/proto-monthly-by-
 * category). SAMPLE: per-category monthly flow series (cat 4/5/11/12) have
 * no serving surface yet — the panel carries its own 'sample' basis chip.
 * Values cycle over however many real Scope 2 months exist.
 */
export interface SampleS3MonthlyFlow {
  cat4Kgco2e: number;
  cat5Kgco2e: number;
  cat11Kgco2e: number;
  cat12Kgco2e: number;
}

export const SAMPLE_S3_MONTHLY_FLOWS_KGCO2E: SampleS3MonthlyFlow[] = [
  { cat4Kgco2e: 400, cat5Kgco2e: 50, cat11Kgco2e: 2_800, cat12Kgco2e: 300 },
  { cat4Kgco2e: 200, cat5Kgco2e: 50, cat11Kgco2e: 2_900, cat12Kgco2e: 200 },
  { cat4Kgco2e: 300, cat5Kgco2e: 50, cat11Kgco2e: 3_100, cat12Kgco2e: 400 },
  { cat4Kgco2e: 500, cat5Kgco2e: 50, cat11Kgco2e: 3_000, cat12Kgco2e: 300 },
  { cat4Kgco2e: 200, cat5Kgco2e: 50, cat11Kgco2e: 3_300, cat12Kgco2e: 200 },
  { cat4Kgco2e: 300, cat5Kgco2e: 50, cat11Kgco2e: 3_200, cat12Kgco2e: 300 },
  { cat4Kgco2e: 400, cat5Kgco2e: 50, cat11Kgco2e: 3_400, cat12Kgco2e: 300 },
];
